import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { User, Mail, Calendar, Save, ArrowLeft, Lock, Shield, Eye, EyeOff, RefreshCw, CheckCircle, AlertCircle, QrCode, Smartphone } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useLocale } from "@/contexts/LocaleContext";

type ProfileTab = "profile" | "security";

const Profile = () => {
  const { t } = useLocale();
  const { user, profile, refreshProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const defaultTab = (searchParams.get("tab") as ProfileTab) || "profile";
  const [tab, setTab] = useState<ProfileTab>(defaultTab);

  const [displayName, setDisplayName] = useState(profile?.display_name || "");
  const [saving, setSaving] = useState(false);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name || "");
    }
  }, [profile]);

  const saveProfile = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("profiles").update({
        display_name: displayName.trim() || null,
      }).eq("id", user.id);
      if (error) throw error;
      await refreshProfile();
      toast({ title: t("profile.updated") });
    } catch (err: any) {
      toast({ title: t("profile.update_failed"), description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast({ title: "Password too short", description: "Minimum 8 characters.", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: t("profile.passwords_dont_match"), variant: "destructive" });
      return;
    }
    setPasswordSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast({ title: t("profile.password_changed") });
      setNewPassword(""); setConfirmPassword("");
    } catch (err: any) {
      toast({ title: t("profile.password_failed"), description: err.message, variant: "destructive" });
    } finally {
      setPasswordSaving(false);
    }
  };

  const tabs = [
    { id: "profile" as const, label: t("profile.title"), icon: User },
    { id: "security" as const, label: t("profile.security"), icon: Shield },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-dark">
      <Helmet>
        <title>Your Profile — JP AI Dorks</title>
        <meta name="description" content="Manage your JP AI Dorks account: update your display name, password, and two-factor authentication settings." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://jp-dork.space/profile" />
        <meta property="og:title" content="Your Profile — JP AI Dorks" />
        <meta property="og:description" content="Personal account settings for JP AI Dorks members." />
        <meta property="og:url" content="https://jp-dork.space/profile" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="fixed inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
      <div className="fixed inset-0 bg-gradient-radial from-primary/5 via-transparent to-transparent pointer-events-none" />
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 relative z-10 max-w-3xl">
        <h1 className="sr-only">Your Profile — JP AI Dorks account settings</h1>

        <div className="flex items-center gap-3 mb-6">
          <Link to="/" className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t("profile.back")}
          </Link>
        </div>


        <div className="flex gap-1 mb-6 p-1 bg-muted/20 rounded-xl border border-border/30 w-fit">
          {tabs.map((tb) => (
            <button key={tb.id} onClick={() => setTab(tb.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === tb.id ? "bg-primary/20 text-primary border border-primary/30" : "text-muted-foreground hover:text-foreground"}`}>
              <tb.icon className="w-4 h-4" />
              {tb.label}
            </button>
          ))}
        </div>

        {tab === "profile" && (
          <div className="space-y-4">
            <div className="glass-card p-6 space-y-5">
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                <User className="w-4 h-4 text-primary" /> {t("profile.profile_info")}
              </h3>

              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-2xl font-bold text-primary-foreground">
                  {(profile?.display_name || user?.email || "?")[0].toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground">{profile?.display_name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t("profile.display_name")}</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={t("profile.display_name_placeholder")}
                      className="w-full pl-9 pr-4 py-2.5 bg-muted/30 border border-border/50 rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t("profile.email")} <span className="text-muted-foreground/60">{t("profile.email_readonly")}</span></label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input value={user?.email || ""} disabled
                      className="w-full pl-9 pr-4 py-2.5 bg-muted/20 border border-border/30 rounded-lg text-sm text-muted-foreground opacity-60 cursor-not-allowed" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t("profile.member_since")}</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"} disabled
                      className="w-full pl-9 pr-4 py-2.5 bg-muted/20 border border-border/30 rounded-lg text-sm text-muted-foreground opacity-60 cursor-not-allowed" />
                  </div>
                </div>
              </div>

              <button onClick={saveProfile} disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-primary to-secondary text-primary-foreground font-semibold text-sm transition-all hover:shadow-neon disabled:opacity-50">
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? t("profile.saving") : t("profile.save_changes")}
              </button>
            </div>
          </div>
        )}

        {tab === "security" && (
          <div className="space-y-4">
            <div className="glass-card p-6 space-y-4">
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                <Lock className="w-4 h-4 text-primary" /> {t("profile.change_password")}
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t("profile.new_password")}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input type={showPasswords ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t("profile.new_password_placeholder")}
                      className="w-full pl-9 pr-10 py-2.5 bg-muted/30 border border-border/50 rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" />
                    <button type="button" onClick={() => setShowPasswords(!showPasswords)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t("profile.confirm_password")}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input type={showPasswords ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t("profile.confirm_password_placeholder")}
                      className="w-full pl-9 pr-4 py-2.5 bg-muted/30 border border-border/50 rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" />
                  </div>
                </div>
                {confirmPassword && newPassword !== confirmPassword && (
                  <div className="flex items-center gap-2 text-destructive text-xs bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
                    <AlertCircle className="w-3.5 h-3.5" /> {t("profile.passwords_dont_match")}
                  </div>
                )}
                <button onClick={changePassword} disabled={passwordSaving || !newPassword || newPassword !== confirmPassword}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-primary to-secondary text-primary-foreground font-semibold text-sm transition-all hover:shadow-neon disabled:opacity-50">
                  {passwordSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  {passwordSaving ? t("profile.changing") : t("profile.change_password")}
                </button>
              </div>
            </div>

            <div className="glass-card p-6 border border-border/50">
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2 mb-3">
                <Smartphone className="w-4 h-4 text-primary" /> {t("profile.2fa_title")}
              </h3>
              <p className="text-xs text-muted-foreground mb-4">{t("profile.2fa_desc")}</p>
              <div className="flex items-center gap-3 p-3 bg-muted/20 border border-border/30 rounded-lg">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <QrCode className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <div className="text-xs font-medium text-foreground">{t("profile.2fa_totp")}</div>
                  <div className="text-xs text-muted-foreground">Enable via auth settings</div>
                </div>
              </div>
            </div>

            <div className="glass-card p-5 border border-border/50">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
                <Mail className="w-4 h-4 text-primary" /> {t("profile.email_verification")}
              </h3>
              <div className={`flex items-center gap-2 text-sm ${user?.email_confirmed_at ? "text-primary" : "text-destructive"}`}>
                {user?.email_confirmed_at ? (
                  <><CheckCircle className="w-4 h-4" /> {t("profile.verified_on", { date: new Date(user.email_confirmed_at).toLocaleDateString() })}</>
                ) : (
                  <><AlertCircle className="w-4 h-4" /> {t("profile.not_verified")}</>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Profile;
